import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link.svg'
import { ReactComponent as ModalCloseIcon } from 'src/assets/svg/modal-close.svg'
import { ReactComponent as TrashIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/trash.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { useSubmitReviewAnalysisLocationsMutation } from 'src/reactQuery/useSubmitReviewAnalysisLocationsMutation'
import styles from './nameLocationsModal.module.scss'

interface LocationEntry {
  name: string
  url: string
}

interface NameLocationsModalProps {
  isOpen: boolean
  onBack: () => void
  onClose: () => void
  onSave: (locations: LocationEntry[]) => void
  urls: string[]
}

export const NameLocationsModal: FC<NameLocationsModalProps> = ({
  isOpen,
  onBack,
  onClose,
  onSave,
  urls,
}) => {
  const [locations, setLocations] = useState<LocationEntry[]>(
    urls.map((url) => ({ name: '', url }))
  )

  useEffect(() => {
    if (isOpen) {
      setLocations(urls.map((url) => ({ name: '', url })))
    }
  }, [isOpen, urls.join(',')])

  useDisableScroll(isOpen)

  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const submitLocations = useSubmitReviewAnalysisLocationsMutation(campaignId)

  if (!isOpen) return null

  const allNamed =
    locations.length > 0 && locations.every((loc) => loc.name.trim().length > 0)

  const handleNameChange = (index: number, name: string) => {
    setLocations((prev) =>
      prev.map((loc, i) => (i === index ? { ...loc, name } : loc))
    )
  }

  const handleDelete = (index: number) => {
    setLocations((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    if (!allNamed) return
    submitLocations.mutate(
      { locations },
      {
        onSettled: () => {
          onSave(locations)
          onClose()
        },
      }
    )
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Name each location</h2>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <ModalCloseIcon />
          </button>
        </div>

        {/* Step progress — both steps active (step 2) */}
        <div className={styles.steps}>
          <div className={styles.step} />
          <div className={styles.step} />
        </div>

        {/* Subtitle */}
        <p className={styles.subtitle}>
          Give each location a name. You can also remove any you don&apos;t
          need.
        </p>

        {/* Location list */}
        <div className={styles.locationList}>
          {locations.map((loc, index) => (
            <div className={styles.locationItem} key={loc.url}>
              <div className={styles.locationUrl}>
                <LinkIcon className={styles.linkIcon} />
                <span>{loc.url}</span>
              </div>
              <div className={styles.locationInputRow}>
                <input
                  className={styles.locationInput}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  placeholder="location name, e.g Head Office"
                  type="text"
                  value={loc.name}
                />
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(index)}
                  type="button"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.backBtn} onClick={onBack} type="button">
            Back
          </button>
          <button className={styles.cancelBtn} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            disabled={!allNamed}
            onClick={handleSave}
            type="button"
          >
            Save locations
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
